import {
    Box,
    Text,
    Input,
    Spinner,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverBody,
    Divider,
    InputRightElement,
    InputGroup,
    InputLeftElement,
    Checkbox,
    Flex,
    useColorModeValue,
  } from '@chakra-ui/react';
  import { ChevronDownIcon } from '@chakra-ui/icons';
  import React, { useEffect, useState, useCallback, useRef } from 'react';
  import InfiniteScroll from 'react-infinite-scroll-component';
  import { FiSearch, FiX } from 'react-icons/fi';
  
  function SearchAdaccountAdspresso({
    selectedAccounts,
    onRemoveAccount,
    pageMeta = [],
    isAdAccountFetching,          // used for the list spinner during search
    onLoadMore,
    isForStatusUpload,
    accountOptions,
    onAccountChange,
    onSearchInputChange,
    onSearchInputTouched,
    onInitialisation,
    AdsAccountIdFromParams,
    isAdAccountLoading,
  }) {
    const [searchAdAccountValue, setSearchAdAccountValue] = useState('');
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const { pageSize, after, hasNextPage } = pageMeta;
    const popoverRef = useRef(null);
  
    const accountListColor = useColorModeValue('gray.800', 'whiteAlpha.900');
    const accountListHoverBg = useColorModeValue('brand.50', '#f5f3fa57');
  
    const filteredAccountsData =
      accountOptions
        ?.filter(
          (account) =>
            account?.name?.toLowerCase().includes(searchAdAccountValue.toLowerCase()) ||
            account?.account_id?.includes(searchAdAccountValue)
        )
        .map((account) => ({
          value: account?.account_id,
          label:
            account?.name?.length > 38 ? account?.name?.slice(0, 40) + '...' : account?.name,
          account,
        })) || [];
  
    useEffect(() => {
      if (filteredAccountsData.length > 0) {
        setFilteredAccounts((prev) => {
          const existingAccountValues = new Set(prev.map((account) => account.value));
          const newAccounts = filteredAccountsData.filter(
            (account) => !existingAccountValues.has(account.value)
          );
          return [...prev, ...newAccounts];
        });
      }
    }, [accountOptions]);
  
    useEffect(() => {
      const delayDebounceFn = setTimeout(() => {
        if (searchAdAccountValue?.length > 2) {
          onSearchInputChange?.(searchAdAccountValue);
          onSearchInputTouched?.(true);
        } else if (searchAdAccountValue?.length === 0) {
          onSearchInputChange?.('');
        }
      }, 400);
  
      return () => clearTimeout(delayDebounceFn);
    }, [searchAdAccountValue, onSearchInputChange, onSearchInputTouched]);
  
    const handleScrollToBottom = useCallback(() => {
      if (hasNextPage && !isAdAccountFetching) {
        onLoadMore?.(after);
      }
    }, [hasNextPage, after, isAdAccountFetching, onLoadMore]);
  
    // when typing, show only filtered list; otherwise the accumulated list
    const isSearching = searchAdAccountValue.length > 0;
    const accountsToShow = isSearching ? filteredAccountsData : filteredAccounts;
  
    useEffect(() => {
      const handleScroll = () => {
        if (!popoverRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = popoverRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          handleScrollToBottom();
        }
      };
  
      const popoverEl = popoverRef.current;
      if (popoverEl) {
        popoverEl.addEventListener('scroll', handleScroll);
      }
  
      return () => {
        if (popoverEl) {
          popoverEl.removeEventListener('scroll', handleScroll);
        }
      };
    }, [handleScrollToBottom]);
  
    return (
      <Box width="280px" position="relative">
        <Popover
          trigger="click"
          placement="bottom-start"
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        >
          <PopoverTrigger>
            <Button
              variant="outline"
              width="320px"
              padding="10px"
              fontSize="13px"
              textAlign="left"
              size="sm"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              fontWeight={'normal'}
              onClick={() => setIsOpen(true)}
              _hover={{ borderColor: 'gray.500' }}
              borderColor={isOpen ? 'gray.500' : 'gray.300'}
            >
              {isAdAccountLoading ? (
                <>
                  Loading... <Spinner size="sm" ml={2} />
                </>
              ) : (
                <>
                  <Box
                    maxWidth="240px"
                    overflowX="auto"
                    whiteSpace="nowrap"
                    css={{
                      '&::-webkit-scrollbar': { height: '2px' },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#CBD5E0',
                        borderRadius: '8px',
                      },
                    }}
                  >
                    {selectedAccounts && selectedAccounts.length > 0
                      ? selectedAccounts
                          .map((acc) => acc?.adsAccountName || acc?.name)
                          .join(', ')
                      : 'Select an Ad Account'}
                  </Box>
                  <ChevronDownIcon boxSize={5} />
                </>
              )}
            </Button>
          </PopoverTrigger>
  
          <PopoverContent
            width="370px"
            ref={popoverRef}
            id={'scrollable-menu'}
            maxHeight="260px"
            overflowY="auto"
            border="1px solid"
            borderColor={'gray.100'}
            borderRadius="md"
          >
            <PopoverArrow />
            <PopoverBody>
              {/* Search Input */}
              <Box>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" mt={-1}>
                    <FiSearch size={18} />
                  </InputLeftElement>
                  <Input
                    borderRadius={'md'}
                    value={searchAdAccountValue}
                    onChange={(e) => setSearchAdAccountValue(e.target.value)}
                    placeholder="Search for an ad account"
                    size="sm"
                  />
                  {searchAdAccountValue && (
                    <InputRightElement mt={-1}>
                      <FiX size={18} cursor="pointer" onClick={() => setSearchAdAccountValue('')} />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
  
              <Divider my={2} />
  
              {/* LIST AREA */}
              <Box>
                {/* 1) Show inline spinner while searching (request in-flight) */}
                {isSearching && isAdAccountFetching && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <Spinner size="sm" mr={2} />
                    <Text fontSize="sm">Searching…</Text>
                  </Box>
                )}
  
                {/* 2) No results state (only after search + not fetching) */}
                {isSearching && !isAdAccountFetching && accountsToShow.length === 0 && (
                  <Box textAlign="center" py={2}>
                    <Text fontSize="sm" color="gray.500">
                      No ad accounts found.
                    </Text>
                  </Box>
                )}
  
                {/* 3) Results list (InfiniteScroll disabled during search) */}
                {accountsToShow.length > 0 && (
                  <InfiniteScroll
                    dataLength={accountsToShow.length}
                    next={handleScrollToBottom}
                    hasMore={!isSearching && hasNextPage}
                    loader={
                      !isSearching ? (
                        <Box display="flex" justifyContent="center" padding="10px">
                          <Spinner size="sm" />
                        </Box>
                      ) : null
                    }
                    scrollThreshold={0.9}
                    scrollableTarget="scrollable-menu"
                  >
                    <Box>
                      {accountsToShow?.map((account) => {
                        const isChecked = selectedAccounts?.some((acc) =>
                          isForStatusUpload
                            ? acc.account_id === account.value
                            : acc.adsAccountId === account.value
                        );
  
                        return (
                          <Box
                            key={account.value}
                            padding="8px"
                            cursor="pointer"
                            bg={isChecked ? 'brand.100' : 'transparent'}
                            color={'black'}
                            fontSize={'12px'}
                            _hover={isChecked ? {} : { backgroundColor: accountListHoverBg }}
                          >
                            <Flex
                              as="label"
                              htmlFor={`checkbox-${account.value}`}
                              align="center"
                              w="100%"
                              cursor="pointer"
                            >
                              <Checkbox
                                id={`checkbox-${account.value}`}
                                isChecked={isChecked}
                                mr={2}
                                colorScheme="brand"
                                size="sm"
                                zIndex={100}
                                onChange={() => {
                                  isChecked
                                    ? onRemoveAccount?.(account.value)
                                    : onAccountChange?.(account);
                                }}
                              />
  
                              <Flex justify="space-between" align="center" w="100%">
                                <Text fontSize="xs" color={isChecked ? 'inherit' : accountListColor}>
                                  {account.label}
                                </Text>
                                <Text fontSize="xs" color={isChecked ? 'inherit' : accountListColor}>
                                  ({account.account.currency})
                                </Text>
                              </Flex>
                            </Flex>
                          </Box>
                        );
                      })}
                    </Box>
                  </InfiniteScroll>
                )}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    );
  }
  
  export default SearchAdaccountAdspresso;
  